/**
 * Read environment variable from multiple sources with fallback
 * Priority: window._env_ > import.meta.env.VITE_* > fallback
 */
export function readEnv(key: string, fallback: string = ''): string {
  try {
    const win = typeof window !== 'undefined' ? window : undefined
    const fromWindow = win && (win as any)._env_ && (win as any)._env_[key]
    const fromImportMeta = (import.meta as any).env?.[`VITE_${key}`]
    return fromWindow || fromImportMeta || fallback
  }
  catch (e) {
    return fallback
  }
}

const LOCAL_API_URL = 'http://localhost:5001'
const UAT_API_URL = 'https://uat-hospitality.ucu.ac.ug/hospitality-api'
const PRODUCTION_API_URL = 'https://hospitality.ucu.ac.ug/hospitality-api'

/**
 * Determines the API URL from the browser hostname.
 * This is used as a reliable override to prevent a misconfigured env.js
 * from sending production traffic to the wrong backend.
 */
function hostnameApiUrl(): string | null {
  try {
    const hostname = typeof window !== 'undefined' ? window.location.hostname : ''

    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return LOCAL_API_URL
    }

    // Must check UAT subdomains BEFORE the broader hospitality.ucu.ac.ug check
    if (
      hostname === 'uat-hospitality.ucu.ac.ug'
      || hostname.endsWith('.uat-hospitality.ucu.ac.ug')
      || hostname.includes('uat-alpha.ucu.ac.ug')
      || hostname.includes('mis-former.ucu.ac.ug')
    ) {
      return UAT_API_URL
    }

    if (
      hostname === 'hospitality.ucu.ac.ug'
      || hostname.endsWith('.hospitality.ucu.ac.ug')
      || hostname.includes('alpha.ucu.ac.ug')
    ) {
      return PRODUCTION_API_URL
    }
  }
  catch {
    // ignore
  }
  return null
}

function resolveApiUrl(): string {
  // Hostname always wins — prevents a stale/wrong env.js from routing
  // production traffic to the wrong environment.
  const fromHostname = hostnameApiUrl()
  if (fromHostname) return fromHostname

  // Fall back to runtime-injected or build-time value
  return readEnv('API_URL', PRODUCTION_API_URL)
}

// Export environment variables
export const API_URL = resolveApiUrl()
export const DEPLOYMENT_ENV = readEnv('DEPLOYMENT_ENV', 'production')
